import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ResourceCard from '@/components/feature/ResourceCard';
import { articles, resources } from '@/mocks/resources';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.id === slug || a.resourceId === slug);
  const relatedResources = resources
    .filter((r) => r.categoryId === article?.categoryId && r.id !== article?.resourceId)
    .slice(0, 4);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-800 mb-2">资源未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该资源可能已下架或链接不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '老六资源库',
    },
    publisher: {
      '@type': 'Organization',
      name: '老六资源库',
    },
  };

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading leading-tight">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed">
                {article.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views.toLocaleString()} 浏览
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-download-line"></i>
                  {article.downloads >= 10000 ? `${(article.downloads / 10000).toFixed(1)}万` : article.downloads.toLocaleString()} 下载
                </span>
                <span>大小：{article.fileSize}</span>
                <span>格式：{article.fileType}</span>
              </div>
            </div>

            <div className="relative mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover rounded-lg"
                title={article.title}
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none mb-10">
              {paragraphs.map((para, index) => {
                if (para.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-3 font-heading">
                      {para.replace('## ', '')}
                    </h2>
                  );
                }
                if (para.startsWith('### ')) {
                  return (
                    <h3 key={index} className="text-base md:text-lg font-semibold text-foreground-800 mt-6 mb-2 font-heading">
                      {para.replace('### ', '')}
                    </h3>
                  );
                }
                if (para.startsWith('- ')) {
                  const items = para.split('\n').filter((line) => line.startsWith('- '));
                  return (
                    <ul key={index} className="list-disc pl-5 mb-4 space-y-1">
                      {items.map((item, i) => (
                        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                          {item.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '$1')}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (para.match(/^\d+\./)) {
                  const items = para.split('\n').filter((line) => /^\d+\./.test(line));
                  return (
                    <ol key={index} className="list-decimal pl-5 mb-4 space-y-1">
                      {items.map((item, i) => (
                        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                          {item.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '$1')}
                        </li>
                      ))}
                    </ol>
                  );
                }
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {para.replace(/\*\*(.*?)\*\*/g, '$1')}
                  </p>
                );
              })}
            </div>

            <div className="flex items-center justify-center p-6 bg-primary-50 rounded-lg border border-primary-200 mb-10">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-500 text-white">
                  <i className="ri-download-2-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">
                  下载资源
                </h3>
                <p className="text-sm text-foreground-600 mb-4">
                  文件大小：{article.fileSize} | 格式：{article.fileType}
                </p>
                <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-download-cloud-2-line text-lg"></i>
                  立即下载
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedResources.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-lg font-semibold text-foreground-900 mb-6 font-heading">
                相关资源推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedResources.map((res) => (
                  <ResourceCard key={res.id} resource={res} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}